import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    InsertDriveFile as FileIcon,
    Image as ImageIcon,
    PictureAsPdf as PdfIcon,
    Description as DocIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { showToast } from '@utils';
import {
    useCreateFileConfigMutation,
    useGetFileConfigsQuery
} from '../../../../../../store/feature/chat/knowledgeBase';

const UploadFile = ({ onBack }) => {
    const { data: fileConfigs, isLoading, refetch } = useGetFileConfigsQuery();

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);

    // Get file icon based on type
    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop()?.toLowerCase();

        switch (extension) {
            case 'pdf':
                return <PdfIcon sx={{ color: '#d32f2f' }} />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return <ImageIcon sx={{ color: '#1976d2' }} />;
            case 'doc':
            case 'docx':
                return <DocIcon sx={{ color: '#1976d2' }} />;
            default:
                return <FileIcon sx={{ color: '#757575' }} />;
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };


    // Only allow txt, pdf, csv
    const allowedExtensions = ['txt', 'pdf', 'csv'];

    // Handle file selection
    const handleFileSelection = (files) => {
        const fileArray = Array.from(files);
        const validFiles = [];
        let blocked = false;
        fileArray.forEach((file) => {
            const ext = file.name.split('.').pop().toLowerCase();
            if (allowedExtensions.includes(ext)) {
                validFiles.push(file);
            } else {
                blocked = true;
            }
        });
        if (blocked) {
            showToast('error', 'Only .txt, .pdf, and .csv files are allowed.');
        }
        if (validFiles.length > 0) {
            setSelectedFiles((prev) => [...prev, ...validFiles]);
        }
    };

    // Handle file input change
    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileSelection(e.target.files);
        }
        // Reset the input value to allow selecting the same file again
        e.target.value = '';
    };

    // Handle drag events
    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    // Handle drop
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelection(e.dataTransfer.files);
        }
    }, []);

    // Remove file from selection
    const removeFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // Clear all selected files
    const clearAllFiles = () => {
        setSelectedFiles([]);
    };

    const [saveFiles] = useCreateFileConfigMutation();

    // Handle upload files when Done is clicked
    const handleUploadFiles = (e) => {
        e.preventDefault();
        if (selectedFiles.length === 0) {
            showToast('warning', 'Please select files to upload');
            return;
        }

        try {
            // Create FormData for multiple files
            const formData = new FormData();

            // Add files to FormData using documents array approach
            const documents = { files: selectedFiles };
            if (documents.files) {
                for (let i = 0; i < documents.files.length; i++) {
                    formData.append('documents', documents.files[i]);
                }
            }

            // Make the API call
            saveFiles(formData)
                .then((resp) => {
                    if (!resp?.error) {
                        showToast(
                            'success',
                            `${selectedFiles.length} file(s) uploaded successfully`
                        );

                        // Clear selected files and refresh file list
                        setSelectedFiles([]);
                        refetch();

                        // Go back to file list
                        onBack();
                    } else {
                        showToast('error', 'Failed to upload files');
                    }
                })
                .catch(() => {
                    showToast('error', 'Failed to upload files');
                });
        } catch (error) {
            showToast('error', 'Error uploading files');
            console.error('Upload error:', error);
        }
    };

    return (
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            {/* Header with Back Button */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    gap: 2
                }}
            >
                <IconButton
                    onClick={onBack}
                    sx={{
                        p: 1,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Upload Files
                </Typography>
            </Box>

            {/* File Upload Area */}
            <Paper
                sx={{
                    p: 4,
                    mb: 3,
                    borderRadius: '8px',
                    border: `2px dashed ${dragActive ? '#1976d2' : '#ccc'}`,
                    backgroundColor: dragActive ? '#f3f8ff' : '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    '&:hover': {
                        borderColor: '#1976d2',
                        backgroundColor: '#f3f8ff'
                    }
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() =>
                    document.getElementById('file-upload-input').click()
                }
            >
                <input
                    id="file-upload-input"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                />

                <CloudUploadIcon
                    sx={{
                        fontSize: 48,
                        color: dragActive ? '#1976d2' : '#666',
                        mb: 2
                    }}
                />

                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {dragActive
                        ? 'Drop files here'
                        : 'Drop files here or click to upload'}
                </Typography>


                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                >
                    Only .txt, .pdf, and .csv files are allowed.
                </Typography>

                <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{ textTransform: 'none' }}
                >
                    Choose Files
                </Button>
            </Paper>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
                <Paper sx={{ p: 3, borderRadius: '8px' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Selected Files ({selectedFiles.length})
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mb: 3
                        }}
                    >
                        {selectedFiles.map((file, index) => (
                            <Box
                                key={`${file.name}-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    backgroundColor: '#fafafa'
                                }}
                            >
                                <Box sx={{ mr: 2 }}>
                                    {getFileIcon(file.name)}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {file.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {formatFileSize(file.size)}
                                    </Typography>
                                </Box>

                                <Tooltip title="Remove">
                                    <IconButton
                                        size="small"
                                        onClick={() => removeFile(index)}
                                        sx={{ ml: 1 }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>

                    {/* Action Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end'
                        }}
                    >
                        <Button variant="outlined" onClick={clearAllFiles}>
                            Clear All
                        </Button>
                        <Button variant="contained" onClick={handleUploadFiles}>
                            Done
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default UploadFile;
